import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { fetchColumns, fetchTasks, addOrUpdateTask, deleteTask, addColumn, deleteColumn, editColumnName, moveColumn } from './firebaseUtils';
import TaskCard from './TaskCard';
import Column from './Column';
import ColumnWrapper from './ColumnWrapper';
import TaskModal from './TaskModal';
import ColumnModal from './ColumnModal';
import '../../css/KanbanApp.css';

const NotesPage = () => {
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState({ title: '', description: '', columnId: '' });
  const [editingTaskId, setEditingTaskId] = useState(null);

  useEffect(() => {
    fetchColumns().then(setColumns);
    fetchTasks().then(setTasks);
  }, []);

  const getColumnTasks = (columnId) => {
    return tasks
      .filter(task => task.columnId === columnId)
      .sort((a, b) => a.order - b.order);
  };

  const handleAddOrUpdateTask = async (task) => {
    const updatedTask = await addOrUpdateTask(task, editingTaskId);
    if (editingTaskId) {
      setTasks(tasks.map(t => t.id === editingTaskId ? updatedTask : t));
    } else {
      setTasks([...tasks, updatedTask]);
    }
    setIsTaskModalOpen(false);
    setCurrentTask({ title: '', description: '', columnId: '' });
    setEditingTaskId(null);
  };

  const handleDeleteTask = async (id) => {
    await deleteTask(id);
    setTasks(tasks.filter(task => task.id !== id));
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setEditingTaskId(task.id);
    setIsTaskModalOpen(true);
  };

  const moveTask = useCallback(async (sourceColumnId, targetColumnId, sourceIndex, targetIndex, taskId) => {
    if (targetColumnId === 'edit') {
      const taskToEdit = tasks.find(task => task.id === taskId);
      handleEditTask(taskToEdit);
      return;
    }
    if (targetColumnId === 'delete') {
      handleDeleteTask(taskId);
      return;
    }

    const updatedTasks = [...tasks];
    const task = updatedTasks.find(t => t.id === taskId);

    if (task) {
      task.columnId = targetColumnId;

      const targetColumnTasks = updatedTasks
        .filter(t => t.columnId === targetColumnId && t.id !== taskId)
        .sort((a, b) => a.order - b.order);

      targetColumnTasks.splice(targetIndex, 0, task);

      targetColumnTasks.forEach((t, i) => {
        t.order = i + 1;
      });

      setTasks(updatedTasks);

      // Actualizar en Firebase
      await Promise.all(targetColumnTasks.map(t => 
        addOrUpdateTask({ ...t, columnId: t.columnId, order: t.order }, t.id)
      ));
    }
  }, [tasks]);

  const handleAddColumn = async ({ name, color }) => {
    if (typeof name !== 'string') {
      console.error("El nombre de la columna debe ser una cadena de texto.");
      return;
    }
  
    try {
      const newColumn = await addColumn(name.trim(), color);
      setColumns([...columns, newColumn]);
    } catch (error) {
      console.error("Error al agregar la columna:", error);
    }
  }; 

  const handleDeleteColumn = async (columnId) => {
    try {
      await deleteColumn(columnId);
      setColumns(prevColumns => prevColumns.filter(col => col.id !== columnId));
      setTasks(prevTasks => prevTasks.filter(task => task.columnId !== columnId));
      console.log(`Columna con ID "${columnId}" eliminada con éxito.`);
    } catch (error) {
      console.error("Error al eliminar la columna:", error);
      // Aquí puedes agregar una notificación al usuario si lo deseas
    }
  };

  const handleEditColumnName = async (columnId, newName) => {
    await editColumnName(columnId, newName);
    setColumns(columns.map(col => col.id === columnId ? { ...col, name: newName } : col));
  };

  const handleMoveColumn = async (dragIndex, hoverIndex) => {
    const reorderedColumns = await moveColumn(columns, dragIndex, hoverIndex);
    setColumns(reorderedColumns);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="kanban-board">
        <h1>Notas</h1>
        <div className="board-actions">
          <button onClick={() => setIsTaskModalOpen(true)} className="add-task-btn">Agregar Tarea</button>
          <button onClick={() => setIsColumnModalOpen(true)} className="add-column-btn">Agregar Columna</button>
        </div>

        <div className="columns">
        {columns.map((column, index) => (
          <ColumnWrapper 
            key={column.id} 
            column={column} 
            index={index} 
            moveColumn={handleMoveColumn}
          >
            <Column
              column={column}
              tasks={getColumnTasks(column.id)}
              moveTask={moveTask}
              onEditColumnName={handleEditColumnName}
              onDeleteColumn={handleDeleteColumn}
            >
              <div
                className="column-header"
                style={{ backgroundColor: column.color || '#ffffff' }} // Color de fondo
              >
                <h3>{column.name}</h3>
              </div>
            </Column>
          </ColumnWrapper>
        ))}
        </div>

        {isTaskModalOpen && (
          <TaskModal
            task={currentTask}
            columns={columns}
            onSubmit={handleAddOrUpdateTask}
            onClose={() => {
              setIsTaskModalOpen(false);
              setCurrentTask({ title: '', description: '', columnId: '' });
              setEditingTaskId(null);
            }}
            isEditing={!!editingTaskId}
          />
        )}

        {isColumnModalOpen && (
          <ColumnModal
            onSubmit={handleAddColumn}
            onClose={() => setIsColumnModalOpen(false)}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default NotesPage;

